import type {
  BrowserCommand,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  DevTunnelRecord,
} from "@cmmd-forge/contracts";
import { normalizeBrowserError } from "@cmmd-forge/shared/browserErrors";
import { DEV_TUNNEL_ACCESS_TOKEN_PARAM } from "@cmmd-forge/shared/devTunnelUrl";
import { localDevServerPreviewPort } from "~/lib/devServerPreviewUrl";
import {
  browserCommandCanChangeActiveAddress,
  shouldRestartBrowserSessionForAddressRecovery,
  type BrowserAddressCommand,
} from "./BrowserPreviewPanel.commands";
import {
  browserSessionHasDifferentDevTunnelForLocalPreview,
  browserSessionHasRecoverableLocalPreviewPort,
  localPreviewSessionAttemptCount,
  preserveLocalPreviewTabsForMaintenanceCommandWithStrategy,
  shouldRecoverLocalPreviewBrowserError,
  shouldRetryLocalPreviewSessionStartup,
} from "./BrowserPreviewPanel.recovery";
import {
  browserDisplayUrl,
  isDevTunnelPreviewUrl,
  isLocalPreviewBrowserUrl,
} from "./BrowserPreviewPanel.url";

export type BrowserOptionsMenuItemId = "restart" | "close" | "clear-data" | "console-logs";

export interface BrowserOptionsMenuItem {
  readonly id: BrowserOptionsMenuItemId;
  readonly label: string;
  readonly disabled: boolean;
}

export type BrowserAddressSubmitAction =
  | "blocked"
  | "open-session"
  | "restart-session"
  | "run-command";

export type BrowserDevServerPreviewAction = "open-session" | "restart-session" | "new-tab";

export class BrowserAttachTimeoutError extends Error {
  constructor(readonly label: string) {
    super(`${label} timed out`);
    this.name = "BrowserAttachTimeoutError";
  }
}

export function isBrowserAttachTimeoutError(error: unknown): error is BrowserAttachTimeoutError {
  return error instanceof BrowserAttachTimeoutError;
}

export function withBrowserOperationTimeout<T>(
  label: string,
  promise: Promise<T>,
  timeoutMs = 100_000,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeout = globalThis.setTimeout(
      () => reject(new BrowserAttachTimeoutError(label)),
      timeoutMs,
    );
    promise.then(
      (value) => {
        globalThis.clearTimeout(timeout);
        resolve(value);
      },
      (error: unknown) => {
        globalThis.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

export function shouldUseDevTunnelForPreview(input: {
  readonly url: string;
  readonly allocatedPort: number | null;
}): boolean {
  const port = localDevServerPreviewPort(input.url);
  return Boolean(port && input.allocatedPort && port === input.allocatedPort);
}

export function hasDevTunnelAccessToken(input: string): boolean {
  try {
    const url = new URL(input);
    return Boolean(url.searchParams.get(DEV_TUNNEL_ACCESS_TOKEN_PARAM));
  } catch {
    return false;
  }
}

export function hasReusableDevTunnelAccess(input: {
  readonly publicUrl: string;
  readonly expiresAt: string;
  readonly nowMs?: number;
}): boolean {
  const expiresAt = Date.parse(input.expiresAt);
  return (
    hasDevTunnelAccessToken(input.publicUrl) &&
    Number.isFinite(expiresAt) &&
    expiresAt > (input.nowMs ?? Date.now())
  );
}

export function upsertDevTunnelRecord(
  records: ReadonlyArray<DevTunnelRecord>,
  next: DevTunnelRecord,
): ReadonlyArray<DevTunnelRecord> {
  const existingIndex = records.findIndex((record) => record.tunnelId === next.tunnelId);
  if (existingIndex === -1) {
    return [...records, next];
  }
  return records.map((record, index) => (index === existingIndex ? next : record));
}

export function mergeDevTunnelRecords(
  baseRecords: ReadonlyArray<DevTunnelRecord>,
  nextRecords: ReadonlyArray<DevTunnelRecord>,
): ReadonlyArray<DevTunnelRecord> {
  return nextRecords.reduce(
    (records, record) => upsertDevTunnelRecord(records, record),
    baseRecords,
  );
}

export function isRecoverableBrowserSessionCommandError(error: unknown): boolean {
  const normalized = normalizeBrowserError(error);
  return (
    normalized.title === "Browser action timed out" ||
    normalized.title === "Browser session is stale"
  );
}

export function shouldRestartLocalPreviewAddressCommand(input: {
  readonly command: BrowserAddressCommand;
  readonly error: unknown;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): boolean {
  return shouldRecoverLocalPreviewBrowserError({
    url: input.command.url,
    error: input.error,
    devTunnelRecords: input.devTunnelRecords,
  });
}

export function preserveLocalPreviewTabsForMaintenanceCommand(input: {
  readonly command: BrowserCommand;
  readonly previousSession: BrowserSessionSnapshot | null;
  readonly nextSession: BrowserSessionSnapshot;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): BrowserSessionSnapshot {
  return preserveLocalPreviewTabsForMaintenanceCommandWithStrategy({
    ...input,
    commandCanChangeActiveAddress: browserCommandCanChangeActiveAddress,
  });
}

export function buildInvalidBrowserAddressMessage(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) {
    return "Enter an http or https URL before opening a browser tab.";
  }
  return "Forge Browser only supports http and https URLs.";
}

export function shouldStartBrowserSession(input: {
  readonly hasSession: boolean;
  readonly connecting: boolean;
}): boolean {
  return !input.hasSession && !input.connecting;
}

export function shouldOpenBrowserSession(input: {
  readonly hasSession: boolean;
  readonly browserBusy: boolean;
  readonly allowWhileConnecting?: boolean;
}): boolean {
  return shouldStartBrowserSession({
    hasSession: input.hasSession,
    connecting: input.allowWhileConnecting === true ? false : input.browserBusy,
  });
}

export function browserSessionUnavailableMessage(): string {
  return "Browser runtime is not available for this environment.";
}

export function browserSessionOpenAttemptCount(input: {
  readonly url: string;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly minimumAttempts: number;
}): number {
  return Math.max(
    input.minimumAttempts,
    localPreviewSessionAttemptCount(input.url, input.devTunnelRecords),
  );
}

export function shouldRetryHiddenLocalPreviewSessionOpen(input: {
  readonly attempt: number;
  readonly attempts: number;
  readonly enabled: boolean;
  readonly url: string;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): boolean {
  return (
    input.enabled &&
    input.attempt < input.attempts &&
    isLocalPreviewBrowserUrl(input.url, input.devTunnelRecords)
  );
}

export function shouldRetryBrowserSessionOpenError(input: {
  readonly attempt: number;
  readonly attempts: number;
  readonly url: string;
  readonly error: unknown;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): boolean {
  return (
    input.attempt < input.attempts &&
    (isBrowserAttachTimeoutError(input.error) ||
      shouldRetryLocalPreviewSessionStartup({
        url: input.url,
        error: input.error,
        devTunnelRecords: input.devTunnelRecords,
      }))
  );
}

export function buildBrowserOptionsMenuItems(input: {
  readonly hasSession: boolean;
  readonly canClearData: boolean;
  readonly canReadConsoleLogs: boolean;
  readonly busy?: boolean;
}): readonly BrowserOptionsMenuItem[] {
  const sessionActionDisabled = !input.hasSession;
  return [
    { id: "restart", label: "Restart browser", disabled: sessionActionDisabled },
    { id: "close", label: "Close browser session", disabled: sessionActionDisabled },
    {
      id: "clear-data",
      label: "Clear browser data",
      disabled: input.busy === true || !input.canClearData,
    },
    {
      id: "console-logs",
      label: "Read console logs",
      disabled: input.busy === true || !input.canReadConsoleLogs,
    },
  ];
}

export function resolveBrowserRestartUrl(input: {
  readonly activeTabUrl: string | null;
  readonly addressValue: string;
  readonly fallbackUrl: string;
}): string {
  const trimmedAddress = input.addressValue.trim();
  return input.activeTabUrl ?? (trimmedAddress.length > 0 ? trimmedAddress : input.fallbackUrl);
}

export function resolveBrowserAddressSubmitAction(input: {
  readonly browserAddressBusy: boolean;
  readonly hasSession: boolean;
  readonly browserActionBusy: boolean;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly hasBlockingError: boolean;
  readonly liveDisplayIsAttached: boolean;
  readonly commandUrl: string;
  readonly session: BrowserSessionSnapshot | null;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): BrowserAddressSubmitAction {
  if (input.browserAddressBusy) {
    return "blocked";
  }
  if (!input.hasSession) {
    return shouldStartBrowserSession({
      hasSession: false,
      connecting: input.browserActionBusy,
    })
      ? "open-session"
      : "blocked";
  }
  if (
    shouldRestartBrowserSessionForAddressRecovery({
      activeTab: input.activeTab,
      hasBlockingError: input.hasBlockingError,
      liveDisplayIsAttached: input.liveDisplayIsAttached,
    })
  ) {
    return "restart-session";
  }

  const localCommandDisplayUrl =
    browserDisplayUrl(input.commandUrl, input.devTunnelRecords) ?? input.commandUrl;
  const localCommandPort = localDevServerPreviewPort(localCommandDisplayUrl);
  if (
    localCommandPort &&
    input.session &&
    (browserSessionHasRecoverableLocalPreviewPort(
      input.session,
      localCommandPort,
      input.devTunnelRecords,
    ) ||
      browserSessionHasDifferentDevTunnelForLocalPreview(
        input.session,
        input.commandUrl,
        input.devTunnelRecords,
      ))
  ) {
    return "restart-session";
  }

  return "run-command";
}

export function resolveBrowserDevServerPreviewAction(input: {
  readonly hasSession: boolean;
  readonly liveDisplayIsAttached: boolean;
  readonly hasBlockingError: boolean;
}): BrowserDevServerPreviewAction {
  if (!input.hasSession) {
    return "open-session";
  }
  return !input.liveDisplayIsAttached || input.hasBlockingError ? "restart-session" : "new-tab";
}

export function resolveDevServerDisplayPreviewUrl(input: {
  readonly previewUrl: string;
  readonly allocatedPort: number;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): string {
  if (isDevTunnelPreviewUrl(input.previewUrl)) {
    return `http://localhost:${input.allocatedPort}/`;
  }
  return (
    browserDisplayUrl(input.previewUrl, input.devTunnelRecords) ??
    `http://localhost:${input.allocatedPort}/`
  );
}
